import { LocateFixed, MapPin, Pencil, X } from 'lucide-react'
import { useState } from 'react'
import { apiErrorMessage } from '../../api/client'
import { profileApi } from '../../api/endpoints'
import { ErrorMessage } from '../ErrorMessage'
import { StateSelect } from '../StateSelect'
import { fillIfEmpty, reverseGeocode } from '../../utils/reverseGeocode'
import type { ProfileResponse } from '../../types/api'

/**
 * A participant's complete address, editable in place.
 *
 * <p>Pincode and needs/services already own their own editors elsewhere - this is the rest of it:
 * house number, street, locality, city, state, country, and the map pin itself. All of it goes
 * through the same {@code PATCH /api/profile} every registration form already writes to, so
 * nothing here is a new capability on the server, only the first UI a signed-in participant has
 * had to reach it after the day they registered.
 */
export function AddressSection({ profile, onSaved }: { profile: ProfileResponse; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)

  const hasAddress = !!(profile.houseNo || profile.addressLine || profile.locality || profile.city || profile.state)

  if (editing) {
    return <AddressForm profile={profile} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); onSaved() }} />
  }

  return (
    <div className="card-pad">
      <div className="flex items-start justify-between gap-3">
        <h2 className="flex items-center gap-2 font-bold text-ink-900">
          <MapPin size={16} className="text-sun-500" /> Your address
        </h2>
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label="Edit address"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-ink-200 hover:bg-ink-100"
        >
          <Pencil size={14} />
        </button>
      </div>

      {hasAddress ? (
        <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {profile.houseNo && (
            <div><dt className="text-ink-500">House / Flat no.</dt><dd className="font-semibold text-ink-900">{profile.houseNo}</dd></div>
          )}
          {profile.addressLine && (
            <div className="sm:col-span-2"><dt className="text-ink-500">Address</dt><dd className="font-semibold text-ink-900">{profile.addressLine}</dd></div>
          )}
          {profile.locality && (
            <div><dt className="text-ink-500">Area / Locality</dt><dd className="font-semibold text-ink-900">{profile.locality}</dd></div>
          )}
          {profile.city && (
            <div><dt className="text-ink-500">City</dt><dd className="font-semibold text-ink-900">{profile.city}</dd></div>
          )}
          {profile.state && (
            <div><dt className="text-ink-500">State</dt><dd className="font-semibold text-ink-900">{profile.state}</dd></div>
          )}
          {profile.country && (
            <div><dt className="text-ink-500">Country</dt><dd className="font-semibold text-ink-900">{profile.country}</dd></div>
          )}
        </dl>
      ) : (
        <p className="mt-2 text-sm text-ink-500">
          You have not added your complete address yet. Add it so tutors know exactly where you are.
        </p>
      )}
    </div>
  )
}

function AddressForm({
  profile,
  onCancel,
  onSaved,
}: {
  profile: ProfileResponse
  onCancel: () => void
  onSaved: () => void
}) {
  const [houseNo, setHouseNo] = useState(profile.houseNo ?? '')
  const [addressLine, setAddressLine] = useState(profile.addressLine ?? '')
  const [locality, setLocality] = useState(profile.locality ?? '')
  const [pincode, setPincode] = useState(profile.pincode ?? '')
  const [city, setCity] = useState(profile.city ?? '')
  const [state, setState] = useState(profile.state ?? '')
  const [country, setCountry] = useState(profile.country ?? 'India')
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [locating, setLocating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  function useCurrentLocation() {
    if (!navigator.geolocation) return
    setLocating(true)
    setError(null)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords
        setCoords({ lat: latitude, lng: longitude })
        try {
          const found = await reverseGeocode(latitude, longitude)
          setLocality((v) => fillIfEmpty(v, found.area ?? found.city))
          setCity((v) => fillIfEmpty(v, found.city))
          setState((v) => fillIfEmpty(v, found.state))
          setAddressLine((v) => fillIfEmpty(v, found.addressLine))
          setPincode((v) => fillIfEmpty(v, found.pincode))
        } catch (err) {
          console.error('[address-section] could not look up the address', err)
          setError('Location pinned, but the address could not be looked up - please type it in.')
        } finally {
          setLocating(false)
        }
      },
      () => {
        setLocating(false)
        setError('Could not get your location. Check the browser has permission, or type it in.')
      },
      { timeout: 8000 },
    )
  }

  async function save() {
    setError(null)
    if (pincode && !/^\d{6}$/.test(pincode)) {
      setError('Enter a valid 6-digit pincode.')
      return
    }
    setSaving(true)
    try {
      await profileApi.updateOwn({
        houseNo: houseNo || undefined,
        addressLine: addressLine || undefined,
        locality: locality || undefined,
        pincode: pincode || undefined,
        city: city || undefined,
        state: state || undefined,
        country: country || undefined,
        lat: coords?.lat,
        lng: coords?.lng,
      })
      onSaved()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save your address'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card-pad">
      <div className="flex items-start justify-between gap-3">
        <h2 className="flex items-center gap-2 font-bold text-ink-900">
          <MapPin size={16} className="text-sun-500" /> Your address
        </h2>
        <button type="button" onClick={onCancel} aria-label="Close" className="text-ink-400 hover:text-ink-700">
          <X size={17} />
        </button>
      </div>

      <div className="mt-3 space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label">House / Flat no.</span>
            <input value={houseNo} onChange={(e) => setHouseNo(e.target.value)} className="input" />
          </label>
          <label className="block">
            <span className="label">Area / Locality</span>
            <input value={locality} onChange={(e) => setLocality(e.target.value)} className="input" />
          </label>
        </div>
        <label className="block">
          <span className="label">Full address</span>
          <input value={addressLine} onChange={(e) => setAddressLine(e.target.value)} className="input" />
          <span className="mt-1 block text-xs text-ink-400">Your exact address is never shown on the map.</span>
        </label>
        <label className="block">
          <span className="label">Pincode</span>
          <input
            value={pincode}
            onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            pattern="\d{6}"
            className="input !w-32"
            placeholder="e.g. 110016"
          />
          <span className="mt-1 block text-xs text-ink-400">
            Changing this moves your dot on the map - it's re-placed the next time you open it.
          </span>
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label">City</span>
            <input value={city} onChange={(e) => setCity(e.target.value)} className="input" />
          </label>
          <label className="block">
            <span className="label">State</span>
            <StateSelect value={state} onChange={setState} />
          </label>
        </div>
        <label className="block">
          <span className="label">Country</span>
          <input value={country} onChange={(e) => setCountry(e.target.value)} className="input" />
        </label>

        <button
          type="button"
          onClick={useCurrentLocation}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-700 underline-offset-4 hover:underline"
        >
          <LocateFixed size={15} /> {locating ? 'Locating...' : coords ? 'Location set' : 'Use my current location'}
        </button>

        <ErrorMessage message={error} />

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="btn-sun">
            {saving ? 'Saving...' : 'Save address'}
          </button>
        </div>
      </div>
    </div>
  )
}
